import {
  estimatePdfPageCount,
  isLinkedInDocumentUrl,
  linkedInDocumentProblem,
} from '@shared/linkedin-media-rules';

import { supabase } from '@/integrations/supabase/client';

const BUCKET = 'post_media';

export interface UploadedDocument {
  publicUrl: string;
  fileName: string;
  pageCount: number | null;
}

/** The file types the post media picker offers when a post can go to LinkedIn. */
export const DOCUMENT_ACCEPT =
  'application/pdf,.pdf,.ppt,.pptx,.doc,.docx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function isDocumentFile(file: Pick<File, 'name'>): boolean {
  return isLinkedInDocumentUrl(file.name);
}

/**
 * Check a document against LinkedIn's limits (100 MB, 300 pages, PDF/PPT/PPTX/DOC/DOCX) and store it with the
 * post's other media. Stored under the same user folder and name pattern as image uploads, so the publisher
 * can title the LinkedIn post with the file name. It does not create a Media library row: the library shows images
 * and video.
 */
export async function uploadPostDocument(file: File): Promise<UploadedDocument> {
  let pageCount: number | null = null;
  if (/\.pdf$/i.test(file.name) && file.size > 0 && file.size <= 100 * 1024 * 1024) {
    pageCount = estimatePdfPageCount(new Uint8Array(await file.arrayBuffer()));
  }
  const problem = linkedInDocumentProblem({ name: file.name, sizeBytes: file.size, pageCount });
  if (problem) throw new Error(problem);

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in to add media');

  const sanitized = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const path = `${user.id}/${Date.now()}-${sanitized}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(`The document could not be uploaded. ${error.message}`);

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return { publicUrl, fileName: file.name, pageCount };
}
